import { IRegEdSubmissionMetadata } from '@Queries/RegEdCompliance';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { MultiContainerHeader } from '../../../../components/MultiContainerHeader';
import { IEditSocialMediaPostContext } from '../context';
import { MobileActionButtons } from './components/MobileActionButtons';
import { MobileContentEditor } from './components/MobileContentEditor';
import { MobileImageCarousel } from './components/MobileImageCarousel';
import { MobileWhereToShare } from './components/MobileWhereToShare';
import { mobileStyles } from './styles';

interface MobileEditSocialMediaPostProps {
	context: IEditSocialMediaPostContext;
	socialMediaConnections: Api.ISocialMediaConnection[];
	onSelectedConnectionsChanged: (selected: Api.IPostTarget[]) => void;
	onRequestClose: () => void;
	onScheduleForLater: () => void;
	onPostNow: () => void;
	onFreeImages?: () => void;
	onUploadMedia?: () => void;
	onShowCancelUpdateConfirmation?: (e: React.MouseEvent<HTMLElement>) => void;
	isSchedulingInProgress: boolean;
	postToEdit?: Api.SocialMediaPostViewModel;
	scheduledPost?: Api.SocialMediaPostViewModel;
	source?: Api.SocialMediaPostReportViewModel | Api.SocialMediaPostViewModel;
	errorMessages?: any;
	isLoaded: boolean;
	usesRegEd?: boolean;
	onScheduleRegEdCompliance?: (e: React.MouseEvent<HTMLElement>, metadata: IRegEdSubmissionMetadata) => void;
	regEdMetadata?: IRegEdSubmissionMetadata | null;
	impersonationCtx?: Api.IImpersonationContext;
}

const MobileEditSocialMediaPost: React.FC<MobileEditSocialMediaPostProps> = observer(
	({
		context,
		socialMediaConnections,
		onSelectedConnectionsChanged,
		onScheduleForLater,
		onPostNow,
		onFreeImages,
		onUploadMedia,
		isSchedulingInProgress,
		source,
		errorMessages,
		isLoaded,
		usesRegEd,
		onScheduleRegEdCompliance,
		regEdMetadata,
		impersonationCtx,
	}) => {
		const userSession = useUserSession();
		const readOnly = source ? !source.canEdit : false;

		const handleContentChange = (content: string) => {
			context.setContent(content);
		};

		const handleImageRemove = (imageId: string) => {
			const updatedImages = context.postImages?.filter(img => img.id !== imageId) || [];
			context.setImages(updatedImages);
		};

		const canPostNow = Boolean(
			context.postTargets?.length &&
				context.postContent?.length &&
				!isSchedulingInProgress &&
				userSession?.user?.id === context.selectedCreator?.id
		);

		const canSchedule = Boolean(context.postTargets?.length && context.postContent?.length && !isSchedulingInProgress);

		const showPostNow = userSession?.user?.id === context.selectedCreator?.id && !readOnly;

		if (!isLoaded) {
			return (
				<div className={css(mobileStyles.container)}>
					<div className={css(mobileStyles.loadingContainer)}>
						<LoadingSpinner type='large' />
						<div className={css(mobileStyles.loadingText)}>Loading post...</div>
					</div>
				</div>
			);
		}
		return (
			<div className={css(mobileStyles.container)}>
				<MultiContainerHeader fullscreenHeader={'Create Post'} />
				<div className={css(mobileStyles.content)}>
					<div className={css(mobileStyles.scrollableContent)}>
						{errorMessages?.messages?.length > 0 && (
							<div className={css(mobileStyles.errorMessage)}>
								{errorMessages.messages.map((msg: string, index: number) => (
									<div key={index}>{msg}</div>
								))}
							</div>
						)}

						{context.selectedCreator && !socialMediaConnections?.length && (
							<div className={css(mobileStyles.warningMessage)}>
								Selected user is currently not linked to any social media accounts.
							</div>
						)}

						{context.selectedCreator && (
							<div className={css(mobileStyles.section)}>
								<MobileWhereToShare
									connections={socialMediaConnections || []}
									selectedTargets={context.postTargets || []}
									onSelectionChanged={onSelectedConnectionsChanged}
									readOnly={readOnly}
								/>
							</div>
						)}

						{context.postImages && context.postImages.length > 0 && (
							<div className={css(mobileStyles.section)}>
								<MobileImageCarousel
									images={context.postImages}
									onImageRemove={readOnly ? undefined : handleImageRemove}
								/>
							</div>
						)}

						<div className={css(mobileStyles.section)}>
							<MobileContentEditor
								content={context.postContent || ''}
								onContentChange={handleContentChange}
								readOnly={readOnly}
							/>
						</div>
					</div>

					{!readOnly && (
						<MobileActionButtons
							onScheduleForLater={onScheduleForLater}
							onPostNow={onPostNow}
							onUploadMedia={onUploadMedia}
							onFreeImages={onFreeImages}
							canPostNow={canPostNow}
							canSchedule={canSchedule}
							isSchedulingInProgress={isSchedulingInProgress}
							showPostNow={showPostNow}
							imageCount={context.postImages?.length || 0}
							usesRegEd={usesRegEd}
							onScheduleRegEdCompliance={onScheduleRegEdCompliance}
							regEdMetadata={regEdMetadata}
							socialProviders={context.postTargets?.map(target => target.provider)}
							impersonationCtx={impersonationCtx}
						/>
					)}
				</div>
			</div>
		);
	}
);

export { MobileEditSocialMediaPost };
